import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { centroid } from '@turf/turf';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useMap } from 'react-map-gl/mapbox';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { showNotification } from 'sonata-react-components';
import { useTheme } from 'styled-components';

import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import { useMapPopover } from '../../MapPopover/MapPopover.context';
import { PolygonConfirmation } from '../../ShapeConfirmation/PolygonConfirmation';
import { getPolygonDrawColors } from './polygon.helpers';

type Props = {
  mapId: string;
};

export function PolygonDrawControl({ mapId }: Props) {
  const { formatMessage } = useIntl();
  const { [mapId]: map } = useMap();
  const drawRef = useRef<MapboxDraw | null>(null);
  const { addPolygon, mode, setMode } = useMultiFlightMap();
  const { closeMapPopover, openMapPopover } = useMapPopover();
  const { uiColors }: any = useTheme();

  const cleanupDraw = () => {
    if (drawRef.current && map?.getMap()) {
      try {
        drawRef.current.changeMode('simple_select', { featureIds: [] });
        drawRef.current.deleteAll();
        map.getMap().removeControl(drawRef.current);
      } catch (e) {
        // biome-ignore lint/suspicious/noConsole: off
        console.warn('Error removing draw control:', e);
      } finally {
        drawRef.current = null;
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeMapPopover();
      cleanupDraw();
      setMode(null);
    }
  };

  const openConfirmation = (coords: [number, number][]) => {
    const mapInstance = map?.getMap();
    if (!mapInstance) {
      return;
    }
    const closedRing = [...coords, coords[0]];
    const polygonFeature = {
      geometry: {
        coordinates: [closedRing],
        type: 'Polygon' as const
      },
      properties: {},
      type: 'Feature' as const
    };
    const [lng, lat] = centroid(polygonFeature).geometry.coordinates;
    const center: [number, number] = [lng, lat];
    const { x, y } = mapInstance.project(center);
    const rect = mapInstance.getContainer().getBoundingClientRect();

    // Defer to the next tick so the click/dblclick that finalised the polygon
    // doesn't reach MapView's onClick handler in the same tick the popover
    // opens — otherwise that handler would dismiss the popover immediately.
    setTimeout(() => {
      openMapPopover({
        render: () => (
          <PolygonConfirmation
            center={center}
            onCancel={() => {
              cleanupDraw();
              setMode(null);
            }}
            onConfirm={async ({ name }) => {
              cleanupDraw();
              setMode(null);
              try {
                await addPolygon(coords, name);
              } catch {
                showNotification(
                  'map',
                  formatMessage({
                    defaultMessage: 'Could not create the polygon. Please try again.',
                    id: 'multiFlightLocations.confirmation.polygon.error'
                  })
                );
              }
            }}
          />
        ),
        x: x + rect.left,
        y: y + rect.top
      });
    }, 0);
  };

  const handleDrawCreate = (e: { features: GeoJSON.Feature[] }) => {
    if (!drawRef.current) {
      return;
    }

    const polygon = e.features[0];
    if (polygon?.geometry?.type === 'Polygon' && polygon.geometry.coordinates[0].length >= 4) {
      const coords = polygon.geometry.coordinates[0].slice(0, -1) as [number, number][];
      openConfirmation(coords);
    }
  };

  useEffect(() => {
    const mapInstance = map?.getMap();

    if (!mapInstance || mode !== 'draw-polygons') {
      cleanupDraw();
      return;
    }

    const draw = new MapboxDraw({
      defaultMode: 'draw_polygon',
      displayControlsDefault: false,
      styles: getPolygonDrawColors(uiColors),
      userProperties: true
    });

    if (drawRef.current) {
      cleanupDraw();
    }

    mapInstance.addControl(draw);
    drawRef.current = draw;

    mapInstance.on('draw.create', handleDrawCreate);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      mapInstance.off('draw.create', handleDrawCreate);
      window.removeEventListener('keydown', handleKeyDown);
      cleanupDraw();
    };
  }, [map, mode]);

  return null;
}
